import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiPhone, FiMail, FiMapPin, FiInstagram } from 'react-icons/fi';
import { SEO_LANDINGS, landingLabel } from '../data/seoLandingPages';

const Footer = () => {
  const isVillaPage = useLocation().pathname.startsWith('/villas/');
  const sealRef = useRef(null);

  useEffect(() => {
    // Load GoDaddy SSL seal script — only on the registered production domain
    if (!window.location.hostname.includes('royalgardenvillas.in')) return;
    const timer = setTimeout(() => {
      try {
        const script = document.createElement('script');
        script.src = 'https://seal.godaddy.com/getSealBasic?sealID=QtHD06CnrjnnGdRAa211RIuqYAG0l5c3kLilQ4TefKG5wh9qLFKCeOwfbMwm';
        script.async = true;
        script.type = 'text/javascript';
        script.onerror = () => {};
        document.body.appendChild(script);
      } catch {}
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <footer data-testid="main-footer" className="bg-[#1B2432]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <h3 className="text-2xl mb-4" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
              <span className="text-[#F57C20]">Royal</span><span className="text-white"> Garden Villas</span>
            </h3>
            <p className="text-white/50 text-xs leading-relaxed mb-4">
              Gated, fully serviced luxury private pool villas on Tata Dam Road, Lonavala — about 5 km from Lonavala station &amp; market. Hosting families, friends and corporate groups from Mumbai &amp; Pune since 2024.
            </p>
            <div className="flex gap-3">
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center rounded-lg border border-white/10 text-white/50 hover:border-[#F57C20] hover:text-[#F57C20] transition-all">
                <FiInstagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <p className="text-[#F57C20] text-[10px] font-semibold tracking-[0.2em] uppercase mb-5">Explore</p>
            <ul className="space-y-2.5">
              {[
                { to: '/villas', label: 'Our Villas' },
                { to: '/booking', label: 'Book Now' },
                { to: '/family-weekend-getaway-lonavala', label: 'Family Weekend Getaways' },
                { to: '/intimate-celebrations-lonavala', label: 'Intimate Celebrations' },
                { to: '/corporate', label: 'Corporate Retreats' },
                { to: '/shooting', label: 'Film Shoots' },
                { to: '/gallery', label: 'Gallery' },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-white/50 hover:text-[#F57C20] text-xs transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Villas */}
          <div>
            <p className="text-[#F57C20] text-[10px] font-semibold tracking-[0.2em] uppercase mb-5">Our Villas</p>
            <ul className="space-y-2.5">
              {[
                { to: '/villas/3bhk-garden-escape-lonavala', label: '3BHK Garden Escape' },
                { to: '/villas/4bhk-garden-retreat-lonavala', label: '4BHK Garden Retreat' },
                { to: '/villas/5bhk-garden-delight-lonavala', label: '5BHK Garden Delight' },
                { to: '/villas/7bhk-harmony-villa-lonavala', label: '7BHK Harmony Villa' },
                { to: '/villas/8bhk-grandeur-villa-lonavala', label: '8BHK Grandeur Villa' },
                { to: '/villas/9bhk-paradise-villa-lonavala', label: '9BHK Paradise Villa' },
                { to: '/villas/12bhk-celebration-villa-lonavala', label: '12BHK Celebration Villa' },
              ].map((link, i) => (
                <li key={i}>
                  <Link to={link.to} className="text-white/50 hover:text-[#F57C20] text-xs transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <p className="text-[#F57C20] text-[10px] font-semibold tracking-[0.2em] uppercase mb-5">Company</p>
            <ul className="space-y-2.5">
              {[
                { to: '/about', label: 'About Us' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/terms', label: 'Terms & Conditions' },
                { to: '/privacy', label: 'Privacy Policy' },
                { to: '/refund-policy', label: 'Refund / Cancellation' },
              ].map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="text-white/50 hover:text-[#F57C20] text-xs transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="text-[#F57C20] text-[10px] font-semibold tracking-[0.2em] uppercase mb-5">Contact</p>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <FiPhone className="w-3.5 h-3.5 text-[#F57C20] mt-0.5 flex-shrink-0" />
                <a href="tel:+919820088292" className="text-white/50 hover:text-white text-xs transition-colors">+91 9820088292</a>
              </li>
              <li className="flex items-start gap-2.5">
                <FiMail className="w-3.5 h-3.5 text-[#F57C20] mt-0.5 flex-shrink-0" />
                <a href="mailto:royalgardenlonavala@gmail.com" className="text-white/50 hover:text-white text-xs transition-colors break-all">royalgardenlonavala@gmail.com</a>
              </li>
              <li className="flex items-start gap-2.5">
                <FiMapPin className="w-3.5 h-3.5 text-[#F57C20] mt-0.5 flex-shrink-0" />
                <span className="text-white/50 text-xs">Royal Garden Villas, Lonavala, Pune District, Maharashtra 410401, India</span>
              </li>
            </ul>
          </div>
        </div>

        <div data-testid="footer-popular-searches" className="border-t border-white/10 mt-14 pt-8">
          <p className="text-white/60 text-[11px] font-semibold tracking-[0.2em] uppercase mb-3">Popular searches</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {SEO_LANDINGS.map((p) => (
              <Link key={p.slug} to={`/${p.slug}`} data-testid={`footer-search-${p.slug}`} className="text-white/45 hover:text-[#F57C20] text-xs transition-colors">{landingLabel(p.slug)}</Link>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-[10px] tracking-wider">
            &copy; {new Date().getFullYear()} Royal Garden Villas. All rights reserved.
          </p>
          <span id="siteseal" ref={sealRef} />
          <div className="flex flex-wrap gap-4 md:gap-6 justify-center">
            <Link to="/about" className="text-white/30 hover:text-white/60 text-[10px] tracking-wider transition-colors">About</Link>
            <Link to="/terms" className="text-white/30 hover:text-white/60 text-[10px] tracking-wider transition-colors">Terms</Link>
            <Link to="/privacy" className="text-white/30 hover:text-white/60 text-[10px] tracking-wider transition-colors">Privacy</Link>
            <Link to="/refund-policy" className="text-white/30 hover:text-white/60 text-[10px] tracking-wider transition-colors">Refund Policy</Link>
          </div>
        </div>
      </div>
      <div aria-hidden="true" className={`rgv-sticky-spacer ${isVillaPage ? 'has-bar' : ''}`} />
    </footer>
  );
};

export default Footer;
